/**
 * @file api-access-requests.ts
 * @description API functions for access request management
 * @author XiaVan Team
 * 
 * Handles user access requests workflow:
 * - Users can request access when not authorized
 * - Admins can approve/deny requests
 * - Emails sent at each step (request, approval, denial)
 * - Creates notifications for admins
 */

import {
  collection,
  doc,
  addDoc,
  getDocs,
  updateDoc,
  Timestamp,
  query,
  where,
  orderBy,
  getDoc,
  setDoc,
} from 'firebase/firestore'
import { db } from './firebase'
import type { AccessRequest, AccessRequestStatus } from '@/types/user'
import { createNotification, deleteNotificationByRequestId } from './api-notifications'

/**
 * Create a new access request
 * Validates that email is Gmail and not already in system
 * 
 * @param email - Gmail address requesting access
 * @param displayName - User's display name from Google
 * @param photoURL - User's photo URL from Google
 * @throws Error if email already exists or is not Gmail
 * @returns Created request ID
 */
export async function createAccessRequest(
  email: string,
  displayName?: string,
  photoURL?: string
): Promise<string> {
  try {
    // Normalize email
    const normalizedEmail = email.toLowerCase().trim()

    // Validate Gmail domain
    if (!normalizedEmail.endsWith('@gmail.com')) {
      throw new Error('Solo se permiten cuentas de Gmail (@gmail.com)')
    }

    // Check if email is already in allowedEmails
    const allowedEmailRef = doc(db, 'allowedEmails', normalizedEmail)
    const allowedEmailDoc = await getDoc(allowedEmailRef)
    if (allowedEmailDoc.exists()) {
      throw new Error('Este email ya tiene acceso. Intenta iniciar sesión.')
    }

    // Check if there's already a pending request
    const requestsRef = collection(db, 'accessRequests')
    const q = query(
      requestsRef,
      where('email', '==', normalizedEmail),
      where('status', '==', 'pending')
    )
    const existingRequests = await getDocs(q)

    if (!existingRequests.empty) {
      throw new Error('Ya existe una solicitud pendiente para este email')
    }

    // Create access request
    const newRequest = {
      email: normalizedEmail,
      displayName: displayName || undefined,
      photoURL: photoURL || undefined,
      status: 'pending' as AccessRequestStatus,
      requestedAt: Timestamp.now(),
    }

    const docRef = await addDoc(requestsRef, newRequest)

    // Send email notification to user (request received)
    await sendAccessRequestEmail(normalizedEmail, 'pending')

    // Create in-app notification for admins
    await createNotification(
      'access-request',
      'Nueva solicitud de acceso',
      `${displayName || normalizedEmail} ha solicitado acceso a XiaVan`,
      docRef.id,
      normalizedEmail
    )

    console.log('📩 Access request created:', normalizedEmail)
    return docRef.id
  } catch (error) {
    console.error('Error creating access request:', error)
    throw error
  }
}

/**
 * Get all access requests
 * @param status - Optional filter by status
 * @returns Array of access requests
 */
export async function getAccessRequests(
  status?: AccessRequestStatus
): Promise<AccessRequest[]> {
  try {
    console.log('🔍 [getAccessRequests] Starting query with status:', status)
    const requestsRef = collection(db, 'accessRequests')
    let q = query(requestsRef, orderBy('requestedAt', 'desc'))

    if (status) {
      console.log('🔍 [getAccessRequests] Using filtered query: status ==', status)
      q = query(requestsRef, where('status', '==', status), orderBy('requestedAt', 'desc'))
    }

    console.log('🔍 [getAccessRequests] Executing Firestore query...')
    const snapshot = await getDocs(q)
    console.log('📊 [getAccessRequests] Query completed. Documents found:', snapshot.size)

    if (snapshot.empty) {
      console.warn('⚠️ [getAccessRequests] No documents found in accessRequests collection')
      console.warn('⚠️ Possible causes:')
      console.warn('  1. No access requests exist in Firestore')
      console.warn('  2. Firestore Security Rules blocking read access')
      console.warn('  3. Missing Firestore index (if using where + orderBy)')
    }

    const requests = snapshot.docs.map((docSnap) => {
      const data = docSnap.data()
      console.log('📄 [getAccessRequests] Document:', docSnap.id, data)
      return {
        id: docSnap.id,
        ...data,
      }
    }) as AccessRequest[]

    console.log('✅ [getAccessRequests] Returning', requests.length, 'requests')
    return requests
  } catch (error) {
    console.error('❌ [getAccessRequests] Error fetching access requests:', error)
    if (error instanceof Error) {
      console.error('Error message:', error.message)
      console.error('Error stack:', error.stack)
    }
    throw error
  }
}

/**
 * Approve an access request
 * Adds email to allowedEmails with audit trail
 * 
 * @param requestId - ID of the request to approve
 * @param adminEmail - Email of admin approving the request
 * @param role - Role to assign (default: 'user')
 */
export async function approveAccessRequest(
  requestId: string,
  adminEmail: string,
  role: 'admin' | 'user' = 'user'
): Promise<void> {
  try {
    // Get request document
    const requestRef = doc(db, 'accessRequests', requestId)
    const requestDoc = await getDoc(requestRef)

    if (!requestDoc.exists()) {
      throw new Error('Solicitud no encontrada')
    }

    const requestData = requestDoc.data()
    const email = requestData.email
    // displayName and photoURL available if needed for future features
    const requestedAt = requestData.requestedAt // Preserve original request date

    // Add to allowedEmails WITH audit trail
    const allowedEmailRef = doc(db, 'allowedEmails', email)
    await setDoc(allowedEmailRef, {
      email,
      role,
      addedBy: adminEmail,
      addedAt: Timestamp.now(),
      requestedAt: requestedAt,    // Date user requested access
      approvedAt: Timestamp.now(),  // Date admin approved
    })

    console.log('✅ Added to allowedEmails:', email)

    // Update request status
    await updateDoc(requestRef, {
      status: 'approved',
      processedAt: Timestamp.now(),
      processedBy: adminEmail,
    })

    // Send approval email to user
    await sendAccessRequestEmail(email, 'approved')

    // Delete related notification
    await deleteNotificationByRequestId(requestId)

    console.log('✅ Access request approved:', email)
  } catch (error) {
    console.error('Error approving access request:', error)
    throw error
  }
}

/**
 * Deny an access request
 * 
 * @param requestId - ID of the request to deny
 * @param adminEmail - Email of admin denying the request
 * @param reason - Optional reason for denial
 */
export async function denyAccessRequest(
  requestId: string,
  adminEmail: string,
  reason?: string
): Promise<void> {
  try {
    const requestRef = doc(db, 'accessRequests', requestId)
    const requestDoc = await getDoc(requestRef)

    if (!requestDoc.exists()) {
      throw new Error('Solicitud no encontrada')
    }

    const requestData = requestDoc.data()
    const email = requestData.email

    // Update request status
    await updateDoc(requestRef, {
      status: 'denied',
      processedAt: Timestamp.now(),
      processedBy: adminEmail,
      denialReason: reason || 'No especificado',
    })

    // Send denial email to user
    await sendAccessRequestEmail(email, 'denied', reason)

    // Delete related notification
    await deleteNotificationByRequestId(requestId)

    console.log('❌ Access request denied:', email)
  } catch (error) {
    console.error('Error denying access request:', error)
    throw error
  }
}

/**
 * Get access request status for a specific email
 * Used to determine what message to show during login flow
 * 
 * @param email - Email to check
 * @returns Status: 'none' | 'pending' | 'approved' | 'denied'
 */
export async function getAccessRequestStatus(
  email: string
): Promise<'none' | 'pending' | 'approved' | 'denied'> {
  try {
    const normalizedEmail = email.toLowerCase().trim()
    
    // Query for most recent request for this email
    const requestsRef = collection(db, 'accessRequests')
    const q = query(
      requestsRef,
      where('email', '==', normalizedEmail),
      orderBy('requestedAt', 'desc')
    )
    
    const snapshot = await getDocs(q)
    
    if (snapshot.empty) {
      return 'none' // No access request found
    }
    
    // Get most recent request
    const latestRequest = snapshot.docs[0].data() as AccessRequest
    
    if (latestRequest.status === 'pending') return 'pending'
    if (latestRequest.status === 'approved') return 'approved'
    if (latestRequest.status === 'denied') return 'denied'
    
    return 'none'
  } catch (error) {
    console.error('Error getting access request status:', error)
    return 'none' // Safe fallback
  }
}

/**
 * Send email notification for access request
 * Uses Firebase Trigger Email extension
 * 
 * @param email - Recipient email
 * @param status - Request status (pending, approved, denied)
 * @param reason - Optional denial reason
 */
async function sendAccessRequestEmail(
  email: string,
  status: 'pending' | 'approved' | 'denied',
  reason?: string
): Promise<void> {
  try {
    const mailRef = collection(db, 'mail')

    let subject = ''
    let html = ''

    switch (status) {
      case 'pending':
        subject = '📩 Solicitud de acceso recibida - XiaVan'
        html = `
          <h2>¡Hola!</h2>
          <p>Hemos recibido tu solicitud de acceso a <strong>XiaVan</strong>.</p>
          <p>Un administrador revisará tu solicitud pronto y recibirás un email con la respuesta.</p>
          <p>Email solicitante: <strong>${email}</strong></p>
          <br>
          <p>Saludos,<br>El equipo de XiaVan 🚐</p>
        `
        break

      case 'approved':
        subject = '✅ Solicitud de acceso APROBADA - XiaVan'
        html = `
          <h2>¡Bienvenido a XiaVan! 🎉</h2>
          <p>Tu solicitud de acceso ha sido <strong>aprobada</strong>.</p>
          <p>Ya puedes iniciar sesión con tu cuenta de Google:</p>
          <p><a href="https://xiavanplans.web.app" style="background-color: #4285F4; color: white; padding: 12px 24px; text-decoration: none; border-radius: 8px; display: inline-block; margin: 16px 0;">Acceder a XiaVan</a></p>
          <br>
          <p>Saludos,<br>El equipo de XiaVan 🚐</p>
        `
        break

      case 'denied':
        subject = '❌ Solicitud de acceso denegada - XiaVan'
        html = `
          <h2>Solicitud de acceso</h2>
          <p>Lamentamos informarte que tu solicitud de acceso a <strong>XiaVan</strong> ha sido denegada.</p>
          ${reason ? `<p><strong>Motivo:</strong> ${reason}</p>` : ''}
          <p>Si crees que esto es un error, puedes contactar con el administrador.</p>
          <br>
          <p>Saludos,<br>El equipo de XiaVan 🚐</p>
        `
        break
    }

    // Add email to queue (Trigger Email extension will process it)
    await addDoc(mailRef, {
      to: email,
      message: {
        subject,
        html,
      },
    })

    console.log(`📧 Email queued: ${email} (${status})`)
  } catch (error) {
    console.error('Error sending email:', error)
    // Don't throw - email is non-critical
  }
}
